import React, { useEffect, useState } from 'react';
import ReviewModal from './ReviewModal';
import SignaturePad from '../../ui/SignaturePad';
import { TextInput, TextArea, DateInput } from '../../ui/form';
import CheckboxCard, { CheckboxInline } from '../../ui/form/CheckboxCard';
import { STEP_LABELS } from '../../application/stepConfig';
import driverApplicationService, { errorMessage } from '../../../services/driverApplicationService';
import { todayIso } from '../../../utils/dateInput';

const REVIEWABLE_STEPS = ['personal', 'license', 'record', 'employment'];

const fieldError = (error, field) => (error?.details || []).find((d) => d.field === field)?.message;

/**
 * Send the application back to the applicant with the sections to fix.
 */
export const RequestChangesModal = ({ open, application, initialSections = [], onClose, onDone }) => {
  const [sections, setSections] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSections(initialSections.length ? initialSections : []);
      setMessage('');
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (step) => setSections((prev) => (prev.includes(step) ? prev.filter((s) => s !== step) : [...prev, step]));
  const ordered = REVIEWABLE_STEPS.filter((s) => sections.includes(s));
  const valid = ordered.length > 0 && message.trim().length >= 5;

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await driverApplicationService.requestChanges(application.id, { sections: ordered, message: message.trim() });
      onDone(result.application, 'Changes requested. The applicant has been notified.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ReviewModal
      open={open}
      title="Request changes"
      description="The applicant can edit only the sections you select, then re-sign and resubmit."
      onClose={onClose}
      onConfirm={confirm}
      confirmLabel="Send request"
      confirmTone="warning"
      confirmDisabled={!valid}
      busy={busy}
      error={error}
    >
      <fieldset>
        <legend className="mb-2 text-sm font-medium text-gray-700">Sections to update <span className="text-red-600" aria-hidden="true">*</span></legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {REVIEWABLE_STEPS.map((step) => (
            <CheckboxCard key={step} name={`flag-${step}`} label={STEP_LABELS[step]} checked={sections.includes(step)} onChange={() => toggle(step)} />
          ))}
        </div>
      </fieldset>
      <TextArea
        label="Message to the applicant"
        required
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="e.g. Please add the phone number for your contact at Ohio Valley Logistics."
        hint="Included in the email and shown at the top of the application."
        maxLength={2000}
      />
    </ReviewModal>
  );
};

/**
 * Close the application as not selected.
 */
export const RejectModal = ({ open, application, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [notifyApplicant, setNotifyApplicant] = useState(true);
  const [includeReason, setIncludeReason] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setNotifyApplicant(true);
      setIncludeReason(false);
      setError('');
    }
  }, [open]);

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await driverApplicationService.reject(application.id, {
        reason: reason.trim() || null,
        notifyApplicant,
        includeReason: notifyApplicant && includeReason && Boolean(reason.trim())
      });
      onDone(result.application, notifyApplicant ? 'Application closed. The applicant has been notified.' : 'Application closed.');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ReviewModal
      open={open}
      title="Not moving forward"
      description="This closes the application. It cannot be reopened; send a new invite if you change your mind."
      onClose={onClose}
      onConfirm={confirm}
      confirmLabel="Close application"
      confirmTone="danger"
      busy={busy}
      error={error}
    >
      <TextArea
        label="Internal reason"
        optionalLabel
        rows={3}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        hint="Kept on the application timeline for your records."
        maxLength={2000}
      />
      <CheckboxInline label="Email the applicant that they were not selected" checked={notifyApplicant} onChange={(e) => setNotifyApplicant(e.target.checked)} />
      {notifyApplicant && (
        <CheckboxInline
          className="pl-8"
          label="Include the reason in the email"
          checked={includeReason}
          disabled={!reason.trim()}
          onChange={(e) => setIncludeReason(e.target.checked)}
        />
      )}
    </ReviewModal>
  );
};

/**
 * Reviewer attestation + signature; produces the final (countersigned) PDF.
 */
export const ApproveSignModal = ({ open, application, legal, currentUser, onClose, onDone }) => {
  const defaultName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ');
  const [typedName, setTypedName] = useState('');
  const [title, setTitle] = useState('');
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      setTypedName(defaultName);
      setTitle(currentUser?.title || currentUser?.jobTitle || '');
      setSignature(null);
      setError('');
      setFieldErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const valid = typedName.trim().length >= 2 && title.trim().length >= 2 && Boolean(signature);

  const confirm = async () => {
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const result = await driverApplicationService.approve(application.id, { typedName: typedName.trim(), title: title.trim(), signatureDataUrl: signature });
      onDone(result.application, result.application.pdfError
        ? 'Approved, but the signed PDF could not be generated. Use "Regenerate PDF".'
        : 'Application approved and signed.');
    } catch (err) {
      setFieldErrors({ typedName: fieldError(err, 'typedName'), title: fieldError(err, 'title') });
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ReviewModal
      open={open}
      size="xl"
      title="Approve & sign"
      description="Your signature is added to the application and a final PDF is generated."
      onClose={onClose}
      onConfirm={confirm}
      confirmLabel="Approve & sign"
      confirmTone="success"
      confirmDisabled={!valid}
      busy={busy}
      busyLabel="Generating final PDF…"
      error={error}
    >
      <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm leading-6 text-gray-800">{legal?.attestation?.text}</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextInput label="Your full name" required value={typedName} onChange={(e) => setTypedName(e.target.value)} autoComplete="name" error={fieldErrors.typedName} />
        <TextInput label="Your job title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Safety Manager" error={fieldErrors.title} />
      </div>
      <div>
        <p className="mb-1.5 text-sm font-medium text-gray-700">Signature <span className="text-red-600" aria-hidden="true">*</span></p>
        <SignaturePad value={signature} onChange={setSignature} height={170} disabled={busy} />
      </div>
    </ReviewModal>
  );
};

const suggestedCode = (prefix, application) => `${prefix}-${String(application?.id || '').slice(-6).toUpperCase()}`;

/**
 * Create the driver record and switch the applicant's account to the driver portal.
 */
export const HireModal = ({ open, application, onClose, onDone }) => {
  const [hireDate, setHireDate] = useState(todayIso());
  const [driverCode, setDriverCode] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      setHireDate(todayIso());
      setDriverCode(suggestedCode('DRV', application));
      setEmployeeId(suggestedCode('EMP', application));
      setError('');
      setFieldErrors({});
    }
  }, [open, application]);

  const valid = Boolean(hireDate) && driverCode.trim() && employeeId.trim();

  const confirm = async () => {
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      const result = await driverApplicationService.hire(application.id, { hireDate, driverCode: driverCode.trim(), employeeId: employeeId.trim() });
      onDone(result.application, result.alreadyHired ? 'This applicant was already hired.' : 'Driver hired. Their account now opens the driver portal.', result.driver);
    } catch (err) {
      const message = errorMessage(err);
      if (err?.code === 'DRIVER_DUPLICATE' || /already exists/i.test(message)) {
        if (/employee/i.test(message)) setFieldErrors({ employeeId: message });
        else if (/driver id|driver code/i.test(message)) setFieldErrors({ driverCode: message });
      }
      if (err?.code === 'DRIVER_PAYLOAD_INVALID' && err.details?.length) {
        setError(`${message}: ${err.details.map((d) => `${d.field} (${d.message})`).join('; ')}`);
      } else {
        setError(message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ReviewModal
      open={open}
      title="Hire driver"
      description="Creates the driver profile from the application, copies the documents, and opens the driver portal for this applicant."
      onClose={onClose}
      onConfirm={confirm}
      confirmLabel="Hire driver"
      confirmTone="success"
      confirmDisabled={!valid}
      busy={busy}
      busyLabel="Creating driver…"
      error={error}
    >
      <DateInput label="Hire / start date" required value={hireDate} onChange={(e) => setHireDate(e.target.value)} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TextInput label="Driver ID" required value={driverCode} onChange={(e) => setDriverCode(e.target.value)} hint="Must be unique in your company" error={fieldErrors.driverCode} />
        <TextInput label="Employee ID" required value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} error={fieldErrors.employeeId} />
      </div>
    </ReviewModal>
  );
};
